// Copyright 2018 Sorama B.V.

import { Point2D } from "datatypes/SoramaWebApi";
import { Unit, UnitValue } from "units/Unit";
import { Rect } from "../IPlotPart";

export interface IGridData {
    readonly grid: Grid;
    readonly data: number[];
    readonly unit: Unit;

    /**
     * Get the minimum and maximum value within a region
     * @param region
     */
    getMinMax(region: Rect, unit: Unit): { min: number; max: number };
}

export enum DataOrder {
    LeftToRightThenTopToBottom,
    LeftToRightThenBottomToTop,
}

export class Grid {
    constructor(
        public dimension: Point2D,
        public spacing: Point2D,
        public center: Point2D,
        public unit: Unit
    ) {
        this.dimension = dimension;
        this.spacing = spacing;
        this.center = center;
        this.unit = unit;
    }

    public size(): Point2D {
        return {
            x: this.spacing.x * this.dimension.x,
            y: this.spacing.y * this.dimension.y,
        };
    }

    public findClosestIndex(point: Point2D, unit: Unit): Point2D {
        // Find the point closest to the provided point
        const s = this.size();
        const xIdx = Math.max(
            Math.min(
                Math.round(
                    (new UnitValue(point.x, unit).toUnit(this.unit) - (this.center.x - s.x / 2)) / this.spacing.x
                ),
                this.dimension.x - 1
            ),
            0
        );
        const yIdx = Math.max(
            Math.min(
                Math.round(
                    (new UnitValue(point.y, unit).toUnit(this.unit) - (this.center.y - s.y / 2)) / this.spacing.y
                ),
                this.dimension.y - 1
            ),
            0
        );

        return {
            x: xIdx,
            y: yIdx,
        };
    }
}

export abstract class GridDataBase implements IGridData {
    private readonly orderFunction: (x: number, y: number) => number;

    protected _grid: Grid;
    public get grid(): Grid {
        return this._grid;
    }

    protected _data: number[];
    public get data(): number[] {
        return this._data;
    }

    protected _unit: Unit;
    public get unit(): Unit {
        return this._unit;
    }

    constructor(data: number[], dataUnit: Unit, dataOrder: DataOrder, grid: Grid) {
        if (data.length != grid.dimension.x * grid.dimension.y)
            throw new Error("GridData: Number of data points does not match grid size");

        this._unit = dataUnit;
        this._data = data;
        this._grid = grid;
        switch (dataOrder) {
            case DataOrder.LeftToRightThenTopToBottom:
                this.orderFunction = (x: number, y: number) => y * this.grid.dimension.x + x;
                break;
            case DataOrder.LeftToRightThenBottomToTop:
                this.orderFunction = (x: number, y: number) =>
                    (this.grid.dimension.y - 1 - y) * this.grid.dimension.x + x;
                break;

            default:
                throw new Error("Not Supported");
        }
    }

    public abstract getMinMax(region: Rect, unit: Unit): { min: number; max: number };

    protected findMinMax(region: Rect, unit: Unit, data: number[]): { min: number; max: number } {
        const minmax = { min: Number.MAX_VALUE, max: -Number.MAX_VALUE };

        const p0 = this.grid.findClosestIndex({ x: region.x, y: region.y }, unit);
        const p1 = this.grid.findClosestIndex({ x: region.x + region.width, y: region.y + region.height }, unit);

        for (let y = p0.y; y <= p1.y; y++) {
            for (let x = p0.x; x <= p1.x; x++) {
                const v = data[this.orderFunction(x, y)];
                minmax.min = Math.min(v, minmax.min);
                minmax.max = Math.max(v, minmax.max);
            }
        }

        return minmax;
    }
}

export class GridData extends GridDataBase {
    public getMinMax(region: Rect, unit: Unit): { min: number; max: number } {
        return this.findMinMax(region, unit, this._data);
    }
}

export class AbsoluteValueGridData extends GridDataBase {
    private readonly absoluteValues: number[];

    constructor(data: number[], absoluteValues: number[], dataUnit: Unit, dataOrder: DataOrder, grid: Grid) {
        super(data, dataUnit, dataOrder, grid);

        if (data.length != absoluteValues.length) {
            throw new Error("Length of arrays do not match");
        }

        this.absoluteValues = absoluteValues;
    }

    public getMinMax(region: Rect, unit: Unit): { min: number; max: number } {
        const minMax = this.findMinMax(region, unit, this.absoluteValues);
        return { min: -minMax.max, max: minMax.max };
    }
}
